/**
 * Loop-safe auto-retry guard for auth errors after OAuth/Clerk redirect.
 *
 * After Clerk OAuth redirect, the session token may not yet be propagated
 * when the first API request fires. A single page reload resolves this.
 * This module ensures at most ONE automatic reload per auth-error episode.
 *
 * Episode definition: a 10-minute window starting from the first auth error.
 * Within one episode, at most 1 page reload is allowed. After 10 minutes
 * without a successful auth response, the episode expires and a new one
 * can start with exactly 1 retry.
 *
 * Worst case: on a permanently broken, abandoned tab, the user gets at most
 * 1 automatic reload per 10 minutes. No infinite loop is possible.
 *
 * Guard rules:
 *  - Only fires on auth errors (401/403), never on 5xx/network errors.
 *  - Sets a JSON sessionStorage flag { at: timestamp, count: 1 } BEFORE the reload.
 *  - On the next page load, if the flag is present and the episode is active
 *    (count >= 1 AND now - at < 10 min) → NO reload.
 *  - After 10 minutes, the episode expires and a new one can start.
 *  - On successful auth (happy path), the flag is only cleared if the episode
 *    has expired (clearAuthAutoRetryFlagIfEpisodeOver). This prevents a parallel
 *    successful loader from resetting the flag while another loader is in its
 *    auth-error episode, which was the root cause of the infinite reload loop.
 */

export const AUTH_AUTO_RETRY_KEY = 'authAutoRetry';
export const EPISODE_TTL_MS = 600_000; // 10 minutes

interface AuthAutoRetryFlag {
  at: number;
  count: number;
}

/**
 * Pure function: should an auto-retry (page reload) be performed?
 *
 * @param now - Current timestamp (ms since epoch), injected for testability.
 * @param storage - Storage-like object (sessionStorage in production).
 * @returns `true` if a reload should happen, `false` otherwise.
 */
export function shouldAutoRetryAuth(now: number, storage: Storage): boolean {
  try {
    const raw = storage.getItem(AUTH_AUTO_RETRY_KEY);

    if (!raw) {
      // First auth error in this episode — set flag and allow retry
      storage.setItem(
        AUTH_AUTO_RETRY_KEY,
        JSON.stringify({ at: now, count: 1 }),
      );
      return true;
    }

    let flag: AuthAutoRetryFlag;
    try {
      flag = JSON.parse(raw);
      if (typeof flag.at !== 'number' || typeof flag.count !== 'number') {
        throw new Error('corrupt');
      }
    } catch {
      // Corrupted value — overwrite and allow retry
      storage.setItem(
        AUTH_AUTO_RETRY_KEY,
        JSON.stringify({ at: now, count: 1 }),
      );
      return true;
    }

    if (flag.count >= 1 && now - flag.at < EPISODE_TTL_MS) {
      // Already retried within this episode — block further reloads
      return false;
    }

    // Episode has expired (>= 10 min) — start a new episode
    storage.setItem(AUTH_AUTO_RETRY_KEY, JSON.stringify({ at: now, count: 1 }));
    return true;
  } catch {
    // storage unavailable → no auto-retry
    return false;
  }
}

/**
 * Clear the auto-retry flag unconditionally.
 * Prefer clearAuthAutoRetryFlagIfEpisodeOver in production code to avoid
 * the mixed-state infinite loop (parallel 200 OK clearing the flag while
 * a 401 loader is in its retry episode).
 */
export function clearAuthAutoRetryFlag(storage: Storage): void {
  storage.removeItem(AUTH_AUTO_RETRY_KEY);
}

/**
 * Episode-safe clear: only removes the flag if the current episode has
 * expired (now - flag.at >= EPISODE_TTL_MS). This prevents a parallel
 * successful loader from resetting the flag while another loader is in
 * its auth-error episode, which was the root cause of the infinite reload loop.
 *
 * Call this on successful auth responses (happy path).
 *
 * @param now - Current timestamp (ms since epoch).
 * @param storage - Storage-like object (sessionStorage in production).
 */
export function clearAuthAutoRetryFlagIfEpisodeOver(
  now: number,
  storage: Storage,
): void {
  try {
    const raw = storage.getItem(AUTH_AUTO_RETRY_KEY);
    if (!raw) return;

    try {
      const flag: AuthAutoRetryFlag = JSON.parse(raw);
      if (typeof flag.at === 'number' && now - flag.at >= EPISODE_TTL_MS) {
        storage.removeItem(AUTH_AUTO_RETRY_KEY);
      }
      // If episode is still active, keep the flag — don't clear it
    } catch {
      // Corrupt flag — remove it
      storage.removeItem(AUTH_AUTO_RETRY_KEY);
    }
  } catch {
    // storage unavailable → no-op
  }
}
